import { useState, useEffect } from "react";
import { hasFeature, normaliseFeatures } from "../utils/features";

// Session cache keyed by username so switching accounts doesn't reuse stale data.
// Envelope: { perms: string[], org: object|null, staff: boolean, at: number }.
const _cache = {};

// Persisted to sessionStorage so a page reload has the answer on the first render
// instead of briefly showing nothing (which read as disabled nav / buttons).
// Short TTL so a role or plan change still propagates without closing the tab.
const PERMS_TTL = 5 * 60 * 1000; // 5 min
// "perms2:" — the Phase 2 envelope carries org/plan/features; older "perms:"
// blobs are ignored so a deploy never serves a stale shape.
const storageKey = (username) => "perms2:" + username;

function readPersisted(username) {
  if (_cache[username]) return _cache[username];
  try {
    const raw = sessionStorage.getItem(storageKey(username));
    if (!raw) return null;
    const env = JSON.parse(raw);
    if (!env || !Array.isArray(env.perms) || Date.now() - env.at > PERMS_TTL) return null;
    _cache[username] = env;
    return env;
  } catch {
    return null;
  }
}

function persist(username, env) {
  _cache[username] = env;
  try {
    sessionStorage.setItem(storageKey(username), JSON.stringify(env));
  } catch {
    // sessionStorage unavailable (private mode / quota) — in-memory cache still works.
  }
}

const EMPTY = { perms: [], org: null, staff: false, at: 0 };

// Fetches the current user's effective permissions, staff flag and organization
// summary (plan, trial end, features) from the proxy (/my-permissions), the
// single source of truth. Shared by usePermissions() and useFeatures().
function useEntitlements() {
  const username = localStorage.getItem("userName");
  const cached = username ? readPersisted(username) : null;
  const [env, setEnv] = useState(cached || EMPTY);
  const [loading, setLoading] = useState(!cached);

  useEffect(() => {
    if (!username) {
      setLoading(false);
      return;
    }
    const hit = readPersisted(username);
    if (hit) {
      setEnv(hit);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const headers = new Headers();
    headers.append("username", username);

    // Retry a transient failure (proxy 503 / network) with backoff instead of
    // treating it as "no permissions" — that was disabling the menu mid-session.
    // A 4xx is authoritative (not signed in / not provisioned): stop immediately
    // with an empty set so guards can show "Access restricted" instead of a
    // blank page while pointless retries run.
    const load = (attempt = 0) => {
      fetch(`${process.env.REACT_APP_BASE_API_URL}my-permissions`, { headers })
        .then((r) => {
          if (r.status >= 400 && r.status < 500) {
            if (!cancelled) {
              setEnv(EMPTY);
              setLoading(false);
            }
            const denied = new Error("perms denied " + r.status);
            denied.final = true;
            throw denied;
          }
          if (r.status !== 200) throw new Error("perms status " + r.status);
          return r.json();
        })
        .then((data) => {
          if (cancelled) return;
          const next = {
            perms: Array.isArray(data.permissions) ? data.permissions : [],
            org: data.org && typeof data.org === "object" ? data.org : null,
            staff: data.staff === true,
            at: Date.now(),
          };
          persist(username, next); // 200 is authoritative — cache it (even if empty)
          setEnv(next);
          setLoading(false);
        })
        .catch((err) => {
          if (cancelled) return;
          if (err && err.final) return; // 4xx already handled: no retries
          if (attempt < 3) {
            setTimeout(() => load(attempt + 1), 1000 * (attempt + 1));
            return; // keep whatever we already have; don't downgrade on a blip
          }
          setLoading(false); // gave up; leave existing state untouched
        });
    };
    load();
    return () => { cancelled = true; };
  }, [username]);

  return { env, loading };
}

// Returns { permissions, has(perm), loading } — unchanged public API.
export default function usePermissions() {
  const { env, loading } = useEntitlements();
  const permissions = env.perms;
  const has = (permission) => permissions.includes(permission);
  return { permissions, has, loading };
}

// Returns the organization's entitlements:
//   features: string[]          granted keys (normalised; ["All"] = everything)
//   has(key): boolean           feature check with parent/All semantics
//   isCsrdOnly: boolean         legacy "CSRD-only" mode (reports but no dashboards/company)
//   plan, planEndsAt, planActive, daysLeft, isActive, orgName, staff, loading
// Staff (no org) and a still-loading state read as "everything allowed" so the
// UI never flashes disabled; the proxy enforces the real answer.
export function useFeatures() {
  const { env, loading } = useEntitlements();
  const org = env.org;
  const features = org ? normaliseFeatures(org.features) : ["All"];
  const unrestricted = loading || env.staff || !org;
  const has = (key) => unrestricted || hasFeature(features, key);
  // "CSRD-only": nothing granted outside reports.frameworks.csrd (containers
  // open via a child grant don't count).
  const isCsrdOnly =
    !unrestricted &&
    features.length > 0 &&
    features.every((k) => k === "reports.frameworks.csrd");
  return {
    features,
    has,
    isCsrdOnly,
    plan: org ? org.plan : null,
    planEndsAt: org ? org.planEndsAt : null,
    planActive: org ? org.planActive !== false : true,
    daysLeft: org ? org.daysLeft : null,
    isActive: org ? org.isActive !== false : true,
    orgName: org ? org.name : null,
    staff: env.staff,
    loading,
  };
}

// Clear cached entitlements (call on logout and at the start of sign-in).
export function clearPermissionsCache() {
  Object.keys(_cache).forEach((k) => delete _cache[k]);
  try {
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith("perms:") || k.startsWith("perms2:"))
      .forEach((k) => sessionStorage.removeItem(k));
  } catch {
    // ignore
  }
}
